import { CSSProperties, ReactNode, useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { ChevronDownIcon } from './icons'
import { PopoverMenu } from './PopoverMenu'
import {
  type SearchableOption,
  type SearchableOptionSearch,
  SearchableOptionMenu
} from './SearchableOptionMenu'

type PrimitiveValue = string | number

export type SearchableSelectProps<T, V extends PrimitiveValue> = {
  title: string
  emptyLabel: string
  value?: V
  className?: string
  style?: CSSProperties
  placeholder?: string
  ariaLabel?: string
  disabled?: boolean
  onChange?: (value: V | undefined) => void
  options: T[]
  buildValue: (opt: T) => V
  buildLabel: (opt: T) => string | number
  buildSearchText?: (opt: T) => string
  buildLeading?: (opt: T) => ReactNode
  buildTrailing?: (opt: T) => ReactNode
  searchItems?: SearchableOptionSearch
  menuClassName?: string
}

export function SearchableSelect<T, V extends PrimitiveValue>({
  title,
  emptyLabel,
  value,
  className,
  style,
  options,
  placeholder,
  ariaLabel,
  onChange,
  buildValue,
  buildLabel,
  buildSearchText,
  buildLeading,
  buildTrailing,
  searchItems,
  disabled,
  menuClassName
}: SearchableSelectProps<T, V>) {
  const [isOpen, setIsOpen] = useState(false)

  const valueSelected = options.some((opt) => buildValue(opt) == value)
  const selectedOption = options.find((opt) => buildValue(opt) == value)
  const triggerLabel = selectedOption ? buildLabel(selectedOption) : placeholder

  const items = useMemo(() => {
    return options.map((opt) => {
      const optionValue = buildValue(opt)

      return {
        id: String(optionValue),
        label: buildLabel(opt),
        searchText: buildSearchText ? buildSearchText(opt) : String(buildLabel(opt)),
        leading: buildLeading?.(opt),
        trailing: buildTrailing?.(opt),
        selected: optionValue == value
      }
    })
  }, [options, value, buildValue, buildLabel, buildSearchText, buildLeading, buildTrailing])

  return (
    <PopoverMenu
      isControlled={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      offset={{ dy: 4 }}
      matchTriggerWidth={true}
      trigger={
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={clsx(
            'relative block w-full rounded-md border-0 bg-white py-2 pl-3 pr-10 text-base ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-gray-300',
            {
              'cursor-not-allowed opacity-50': disabled,
              'text-gray-500': !valueSelected
            },
            className
          )}
          style={style}
        >
          <div
            className={clsx('block min-w-0 truncate text-left', {
              'text-gray-900': valueSelected,
              'text-gray-500': !valueSelected
            })}
          >
            {triggerLabel ?? 'Select an option'}
          </div>
          <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
        </button>
      }
    >
      <SearchableOptionMenu
        title={title}
        placeholder={`Search ${title.toLowerCase()}`}
        emptyLabel={emptyLabel}
        items={items}
        searchItems={searchItems}
        className={clsx('w-full', menuClassName)}
        onRequestClose={() => setIsOpen(false)}
        onSelect={(selectedId) => {
          const selectedOption = options.find((opt) => String(buildValue(opt)) == selectedId)
          if (!selectedOption) {
            return
          }

          onChange?.(buildValue(selectedOption))
          setIsOpen(false)
        }}
      />
    </PopoverMenu>
  )
}
